import { DIALOG_DATA, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ChartToothStateLayer, ToothSurface } from '@ffd/api-client';
import { RequiredInputSurfaceConfiguration, SurfaceSelectorComponent, TooltipModule, ToothWithLayers } from '@libs/ui-lib';
import { calculateImagePath } from '@libs/utils';
import { ConfigService } from '@libs/shared-services';
import { TeethHighlightComponent } from '../teeth-highlight';
import { SurfaceSelectorDialogData, SurfaceSelectorResult } from './surface-selector-dialog.vm';

@Component({
  selector: 'lib-surface-selector-dialog',
  templateUrl: './surface-selector-dialog.component.html',
  styleUrl: './surface-selector-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SvgIconComponent,
    TranslateModule,
    DialogModule,
    TeethHighlightComponent,
    SurfaceSelectorComponent,
    ReactiveFormsModule,
    TooltipModule,
  ],
})
export class SurfaceSelectorDialogComponent implements OnInit {
  protected readonly dialogData: SurfaceSelectorDialogData = inject(DIALOG_DATA);
  private readonly dialogRef = inject<DialogRef<SurfaceSelectorResult>>(DialogRef);

  private readonly configService = inject(ConfigService);

  private readonly initialLayersByPosition = new Map<number, ChartToothStateLayer[]>(
    this.dialogData.selectedTeeth.map((tooth) => [tooth.position, (tooth as ToothWithLayers).currentState?.layers || []]),
  );

  protected selectedIndex = signal(0);
  protected applyButtonDisabled = signal(true);
  protected selectedTeethWithSurface = signal<{ position: number; surfaces: ToothSurface[] }[]>(
    this.dialogData.selectedTeeth.map((tooth) => ({ position: tooth.position, surfaces: [] })),
  );
  protected readonly teeth = signal<ToothWithLayers[]>(this.dialogData.selectedTeeth);

  protected readonly isEditMode = this.dialogData.isEditMode;
  protected readonly selectedTooth = computed(() => this.dialogData.selectedTeeth[this.selectedIndex()]);
  protected readonly selectedPosition = computed(() => this.selectedTooth().position);

  protected readonly surfacesForm = new FormGroup<{ [position: string]: FormControl<ToothSurface[] | null> }>(
    this.dialogData.selectedTeeth.reduce(
      (acc, tooth) => {
        const initialValue = this.dialogData.isEditMode
          ? (this.dialogData.treatment.requiredInputData as RequiredInputSurfaceConfiguration)?.surfaces || []
          : [];
        acc[tooth.position.toString()] = new FormControl<ToothSurface[] | null>(initialValue);
        return acc;
      },
      {} as { [position: string]: FormControl<ToothSurface[] | null> },
    ),
  );

  protected readonly currentToothControl = computed(() => this.surfacesForm.controls[this.selectedPosition().toString()]);

  constructor() {
    this.dialogData.selectedTeeth.forEach((tooth, index) => {
      const control = this.surfacesForm.controls[tooth.position.toString()];
      control.valueChanges.subscribe((surfaces) => {
        if (!surfaces) {
          return;
        }

        this.onSurfaceChange(tooth.position, surfaces);

        this.selectedTeethWithSurface.update((current) => {
          const toothToUpdate = current.find((t) => t.position === tooth.position);
          if (toothToUpdate) {
            toothToUpdate.surfaces = surfaces;
          }
          return current;
        });

        if (index === this.selectedIndex()) {
          this.applyButtonDisabled.set(surfaces.length === 0);
        }
      });
    });

    effect(() => {
      const index = this.selectedIndex();
      const currentControl = this.surfacesForm.controls[this.dialogData.selectedTeeth[index].position.toString()];
      this.applyButtonDisabled.set(!currentControl.value || currentControl.value.length === 0);
    });

    if (this.dialogData.isEditMode) {
      const surfaces = (this.dialogData.treatment.requiredInputData as RequiredInputSurfaceConfiguration)?.surfaces || [];
      this.dialogData.selectedTeeth.forEach((tooth) => {
        this.onSurfaceChange(tooth.position, surfaces);
      });
    }
  }

  public ngOnInit(): void {
    this.selectedIndex.set(0);
  }

  protected onCloseClick(): void {
    this.dialogRef.close();
  }

  protected onToothSelect(position: number): void {
    const index = this.dialogData.selectedTeeth.findIndex((tooth) => tooth.position === position);
    if (index !== -1) {
      this.selectedIndex.set(index);
    }
  }

  protected onModifySelectionClick(): void {
    this.dialogRef.close({
      selectedTeethWithSurface: [],
      modifySelectionConfig: {
        treatment: this.dialogData.treatment,
        selectedTeeth: this.dialogData.selectedTeeth.map((tooth) => tooth.position),
      },
    });
  }

  protected onApplyToAllClick(): void {
    const currentSurfaces = this.currentToothControl().value || [];
    this.dialogRef.close({
      selectedTeethWithSurface: this.dialogData.selectedTeeth.map((tooth) => ({
        position: tooth.position,
        surfaces: currentSurfaces,
      })),
    });
  }

  protected onApplyClick(): void {
    const firstControlWithoutValue = this.dialogData.selectedTeeth.find((tooth) => {
      const control = this.surfacesForm.controls[tooth.position.toString()];
      return !control.value || control.value.length === 0;
    });

    this.selectedTeethWithSurface.update((current) => {
      const toothToUpdate = current.find((tooth) => tooth.position === this.selectedPosition());
      if (toothToUpdate) {
        toothToUpdate.surfaces = this.currentToothControl().value || [];
      }
      return current;
    });
    this.selectedIndex.set(this.selectedIndex() + 1);

    if (this.selectedIndex() >= this.dialogData.selectedTeeth.length && firstControlWithoutValue) {
      this.onToothSelect(firstControlWithoutValue.position);
    }

    if (this.selectedIndex() >= this.dialogData.selectedTeeth.length && !firstControlWithoutValue) {
      this.dialogRef.close({
        selectedTeethWithSurface: this.selectedTeethWithSurface(),
      });
    }
  }

  protected onSurfaceChange(position: number, surfaces: ToothSurface[]): void {
    const teethAssetBasePath = this.configService.config()?.teethAssetBasePath ?? '';
    this.teeth.update((teeth) => {
      return teeth.map((tooth) => {
        if (tooth.position !== position) {
          return tooth;
        }
        const treatment = this.dialogData.treatment;
        const newLayer = {
          url: `${teethAssetBasePath}/${calculateImagePath(position, treatment.assetPath, surfaces)}`,
          treatmentMirror: treatment.treatmentMirror || false,
          status: treatment.status,
        };

        // Get the initial layers for this tooth (before any surface changes)
        // and add only one preview layer on top
        const initialLayers = this.initialLayersByPosition.get(position) || [];
        const updatedLayers = [...initialLayers, newLayer];

        return {
          ...tooth,
          currentState: {
            ...tooth.currentState,
            layers: updatedLayers,
          },
        };
      });
    });
  }
}
